import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { formatIfDefined, getCurrentUserServer } from '@adornis/bh-util/util.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { AdornisTemplater } from '@adornis/templater/server/index.js';
import { format } from 'date-fns';
import { getLegacyGlobalSetting } from './global-settings.js';
import { getParticipationsByID } from './participation-helper.js';
import { getProductsByIDs } from './product.js';
import { getAdornisFileByID } from './files.js';

export const generateMultiCertificate = registerMutation({
  operationName: 'generateMultiCertificate',
  type: () => String,
  params: [
    { name: 'participationsIDs', type: () => [String] },
    { name: 'lang', type: () => String },
  ],
  resolve: (participationIDs: string[], lang?: 'de' | 'en') => async () => {
    const user = await getCurrentUserServer();
    if (!user) throw new Error('user not found');

    const participations = await (async () => {
      // used to generate certificate-preview
      if (participationIDs[0] === 'testing_1' && participationIDs[1] === 'testing_2') {
        return [
          { _id: 'testing_1', userID: user._id, product: { name: 'workshop1' } },
          { _id: 'testing_2', userID: user._id, product: { name: 'workshop2' } },
        ];
      }

      const _participations = await getParticipationsByID(participationIDs)({ _id: 1, userID: 1, productID: 1 });
      const products = await getProductsByIDs(_participations.map(p => p.productID as string))({ _id: 1, name: 1 });
      for (const p of _participations) (p as any).product = products.find(prod => prod._id === p.productID);
      return _participations;
    })();

    // do not allow to mix participations from different users
    for (const participation of participations) {
      if (participation.userID !== user._id) {
        throw new Error('user is not allowed to generate certificates for other users');
      }
    }

    const eventNames = participations.map(participation => {
      if (!participation.product) throw new Error('Participation has no product');
      return participation.product.name;
    });

    const globalSettings = await getLegacyGlobalSetting()({ multiCertTemplateID: 1, multiCertTemplateID_en: 1 });
    const templateID = lang === 'en' ? globalSettings.multiCertTemplateID_en : globalSettings.multiCertTemplateID;
    if (!templateID) throw new Error('No template found');
    const templateFile = await getAdornisFileByID(templateID)(selectionSet(() => AdornisFile, 2));
    if (!templateFile) throw new Error('No template-file found ' + templateID);
    const templater = AdornisTemplater.fromFile(templateFile.getFullPath());
    templater.render({
      vorname: user.firstName,
      nachname: user.lastName,
      geburtsdatum: formatIfDefined(user.dateOfBirth, 'dd.MM.yyyy') || '',
      eventnamen: eventNames.join(', '),
      erstellungsdatum: format(new Date(), 'dd.MM.yyyy'),
    });
    const { protectPdfBuffer } = await import('../server/pdf-protection.js');
    const pdfBuffer = protectPdfBuffer(await templater.toPdfBuffer());
    const file = await AdornisFile.createFromBuffer(pdfBuffer, 'global-certificate.pdf');
    return file._id;
  },
});
